/**
 * Copyright 2024-2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { LogLevel } from "../logging/logger";
import { AsyncPrefixStore, SyncPrefixStore } from "../utils/cache/store";
import { validateStore } from "../utils/cache/store_validator";
import { ExponentialBackoff, IntervalRepeater } from "../utils/repeater/repeater";
import { BatchEventProcessor, DEFAULT_MAX_BACKOFF, DEFAULT_MIN_BACKOFF } from "./batch_event_processor";
import { EVENT_STORE_PREFIX } from "./event_store";
import { ForwardingEventProcessor } from "./forwarding_event_processor";
export const INVALID_EVENT_DISPATCHER = 'Invalid event dispatcher';
export const FAILED_EVENT_RETRY_INTERVAL = 20 * 1000;
export const getPrefixEventStore = (store) => {
    if (store.operation === 'async') {
        return new AsyncPrefixStore(store, EVENT_STORE_PREFIX, JSON.parse, JSON.stringify);
    }
    else {
        return new SyncPrefixStore(store, EVENT_STORE_PREFIX, JSON.parse, JSON.stringify);
    }
};
const eventProcessorSymbol = Symbol();
export const validateEventDispatcher = (eventDispatcher) => {
    if (!eventDispatcher || typeof eventDispatcher !== 'object' || typeof eventDispatcher.dispatchEvent !== 'function') {
        throw new Error(INVALID_EVENT_DISPATCHER);
    }
};
export const getBatchEventProcessor = (options, EventProcessorConstructor = BatchEventProcessor) => {
    const { eventDispatcher, closingEventDispatcher, retryOptions, eventStore } = options;
    validateEventDispatcher(eventDispatcher);
    if (closingEventDispatcher) {
        validateEventDispatcher(closingEventDispatcher);
    }
    if (eventStore) {
        validateStore(eventStore);
    }
    const retryConfig = retryOptions ? {
        maxRetries: retryOptions.maxRetries,
        backoffProvider: () => {
            var _a, _b;
            const minBackoff = (_a = retryOptions === null || retryOptions === void 0 ? void 0 : retryOptions.minBackoff) !== null && _a !== void 0 ? _a : DEFAULT_MIN_BACKOFF;
            const maxBackoff = (_b = retryOptions === null || retryOptions === void 0 ? void 0 : retryOptions.maxBackoff) !== null && _b !== void 0 ? _b : DEFAULT_MAX_BACKOFF;
            return new ExponentialBackoff(minBackoff, maxBackoff, 50);
        }
    } : undefined;
    const startupLogs = [];
    const { defaultFlushInterval, defaultBatchSize } = options;
    let flushInterval = defaultFlushInterval;
    if (options.flushInterval === undefined || options.flushInterval <= 0) {
        startupLogs.push({
            level: LogLevel.Warn,
            message: 'Invalid flushInterval %s, defaulting to %s',
            params: [options.flushInterval, defaultFlushInterval],
        });
    }
    else {
        flushInterval = options.flushInterval;
    }
    let batchSize = defaultBatchSize;
    if (options.batchSize === undefined || options.batchSize <= 0) {
        startupLogs.push({
            level: LogLevel.Warn,
            message: 'Invalid batchSize %s, defaulting to %s',
            params: [options.batchSize, defaultBatchSize],
        });
    }
    else {
        batchSize = options.batchSize;
    }
    const dispatchRepeater = new IntervalRepeater(flushInterval);
    const failedEventRepeater = options.failedEventRetryInterval ?
        new IntervalRepeater(options.failedEventRetryInterval) : undefined;
    return new EventProcessorConstructor({
        eventDispatcher,
        closingEventDispatcher,
        dispatchRepeater,
        failedEventRepeater,
        retryConfig,
        batchSize,
        eventStore,
        startupLogs,
    });
};
export const wrapEventProcessor = (eventProcessor) => {
    return {
        [eventProcessorSymbol]: eventProcessor,
    };
};
export const getOpaqueBatchEventProcessor = (options, EventProcessorConstructor = BatchEventProcessor) => {
    return wrapEventProcessor(getBatchEventProcessor(options, EventProcessorConstructor));
};
export const extractEventProcessor = (eventProcessor) => {
    if (!eventProcessor || typeof eventProcessor !== 'object') {
        return undefined;
    }
    return eventProcessor[eventProcessorSymbol];
};
export function getForwardingEventProcessor(dispatcher) {
    validateEventDispatcher(dispatcher);
    return new ForwardingEventProcessor(dispatcher);
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=event_processor_factory.js.map